import {
  ClearValueButton,
  PasteFromClipboardButton,
  SampleButton,
} from '@drodil/backstage-plugin-toolbox';
import {
  TextField,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Box,
  Paper,
  Button,
  Divider,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import ImageIcon from '@material-ui/icons/Image';
import { useState, useMemo, useRef, useEffect } from 'react';
import { default as React } from 'react';
import Grid from '@material-ui/core/Grid';
import { Prism, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import {
  vscDarkPlus,
  vs,
  materialDark,
  tomorrow,
  oneDark,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import html2canvas from 'html2canvas';
import languages from './languages';
import sampleInputs from './sampleInputs';

// TypeScript compatibility workaround with better error handling
const SyntaxHighlighter = React.memo((props: SyntaxHighlighterProps) => {
  try {
    const Component = Prism as any as React.FC<SyntaxHighlighterProps>;
    return React.createElement(Component, props);
  } catch (error) {
    return (
      <pre
        style={{
          margin: 0,
          padding: '1rem',
          background: '#f5f5f5',
          borderRadius: '4px',
          overflow: 'auto',
          fontFamily: '"Consolas", "Monaco", monospace',
          fontSize: '14px',
        }}
      >
        <code>{props.children}</code>
      </pre>
    );
  }
});

SyntaxHighlighter.displayName = 'SyntaxHighlighter';

const syntaxThemes = {
  'VS Code Dark': vscDarkPlus,
  'VS Code Light': vs,
  'Material Dark': materialDark,
  Tomorrow: tomorrow,
  'GitHub Dark': oneDark,
};

const fileExtensions = {
  python: 'py',
  java: 'java',
  javascript: 'js',
  typescript: 'ts',
  cobol: 'cob',
  json: 'json',
  sql: 'sql',
  xml: 'xml',
};

const calculateOptimalWidth = (code: string, fontSize: number = 14): number => {
  if (!code) return 400;

  const lines = code.split('\n');
  const longestLine = lines.reduce(
    (max, line) => (line.length > max.length ? line : max),
    '',
  );

  const charWidth = fontSize * 0.6;
  const contentWidth = longestLine.length * charWidth;
  const lineNumberWidth =
    Math.max(lines.length.toString().length, 2) * charWidth + 40;
  const padding = 60; // Left-right padding
  const totalWidth = contentWidth + lineNumberWidth + padding;
  const minWidth = 300;
  const maxWidth = Math.min(1200, window.innerWidth * 0.9);

  return Math.max(minWidth, Math.min(maxWidth, totalWidth));
};

const WindowHeader = ({ fileName }: { fileName: string }) => {
  const theme = useTheme();

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.type === 'dark' ? '#2d2d30' : '#f3f3f3',
        borderBottom: `1px solid ${
          theme.palette.type === 'dark' ? '#404040' : '#e0e0e0'
        }`,
        minHeight: '36px',
      }}
    >
      <Box
        style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
      >
        <Typography
          variant="body2"
          style={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            fontSize: '13px',
          }}
        >
          {fileName}
        </Typography>
      </Box>
      <Box style={{ display: 'flex', gap: theme.spacing(0.5) }}>
        <Box
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#ff5f57',
          }}
        />
        <Box
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#ffbd2e',
          }}
        />
        <Box
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#28ca42',
          }}
        />
      </Box>
    </Box>
  );
};

export function CodeSnippetGenerator() {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const [selectedTheme, setSelectedTheme] = useState('VS Code Dark');
  const [copySuccess, setCopySuccess] = useState(false);
  const [imageExporting, setImageExporting] = useState(false);
  const [optimalWidth, setOptimalWidth] = useState(400);
  const codePreviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (input) {
      const newWidth = calculateOptimalWidth(input);
      setOptimalWidth(newWidth);
    }
  }, [input]);

  const handleChange = (event: any) => {
    setInput(event.target.value);
  };

  const handleLanguageChange = (event: any) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
  };

  const handleSample = () => {
    setInput(sampleInputs[selectedLanguage as keyof typeof sampleInputs]);
  };

  const handleClear = () => {
    setInput('');
  };

  const handlePaste = (pasteRegex: string) => {
    setInput(pasteRegex);
  };

  const handleThemeChange = (event: any) => {
    setSelectedTheme(event.target.value);
  };

  const fileName = `example.${
    fileExtensions[selectedLanguage as keyof typeof fileExtensions]
  }`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(input);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // console.error('Failed to copy text: ', err);
    }
  };

  const exportAsImage = async (download: boolean = false) => {
    if (!codePreviewRef.current || !input) return;

    setImageExporting(true);
    try {
      // Wait for any dynamic sizing and rendering to settle
      await new Promise(resolve => setTimeout(resolve, 200));

      const canvas = await html2canvas(codePreviewRef.current, {
        backgroundColor: null,
        scale: 16,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: codePreviewRef.current.scrollWidth,
        height: codePreviewRef.current.scrollHeight,
        logging: false,
        removeContainer: true,
        imageTimeout: 10000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.querySelector(
            '[data-code-preview]',
          ) as HTMLElement;
          if (clonedElement) {
            clonedElement.style.width = `${optimalWidth}px`;
            clonedElement.style.maxWidth = `${optimalWidth}px`;

            const syntaxElements =
              clonedElement.querySelectorAll('code, .token');
            syntaxElements.forEach(el => {
              const element = el as HTMLElement;
              if (element.style) {
                element.style.textRendering = 'optimizeLegibility';
                element.style.setProperty(
                  '-webkit-font-smoothing',
                  'antialiased',
                );
              }
            });
          }
        },
      });

      if (download) {
        const link = document.createElement('a');
        link.download = `${fileName}-snippet.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
      } else {
        canvas.toBlob(
          async blob => {
            if (blob) {
              try {
                await navigator.clipboard.write([
                  new ClipboardItem({ 'image/png': blob }),
                ]);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
              } catch (err) {
                const link = document.createElement('a');
                link.download = `${fileName}-snippet.png`;
                link.href = canvas.toDataURL('image/png', 1.0);
                link.click();
              }
            }
          },
          'image/png',
          1.0,
        );
      }
    } catch (err) {
      // console.error('Failed to export image: ', err);
    } finally {
      setImageExporting(false);
    }
  };

  // Memoize the syntax highlighter props to prevent unnecessary re-renders
  const highlighterProps = useMemo(
    () => ({
      language: selectedLanguage,
      style: syntaxThemes[selectedTheme as keyof typeof syntaxThemes],
      showLineNumbers: true,
      wrapLines: false, // Disabled line wrapping for accurate width calculation
      customStyle: {
        margin: 0,
        fontSize: '14px',
        fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
        borderRadius: 0,
        width: 'fit-content',
        minWidth: '100%',
      },
      codeTagProps: {
        style: {
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
        },
      },
      children: String(input || ''), // Ensure it's always a string
    }),
    [selectedLanguage, selectedTheme, input],
  );

  return (
    <Grid container style={{ maxWidth: '100%' }} spacing={2}>
      <Grid item xs={12}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(2),
          }}
        >
          <PasteFromClipboardButton
            setInput={pasteRegex => handlePaste(pasteRegex)}
          />
          <ClearValueButton setValue={() => handleClear()} />
          <SampleButton
            sample={sampleInputs[selectedLanguage as keyof typeof sampleInputs]}
            setInput={() => handleSample()}
          />
          <FormControl
            variant="standard"
            style={{
              minWidth: 140,
              width: 'auto',
            }}
          >
            <Select
              labelId="language-select-label"
              id="language-select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              label="Language"
            >
              {languages.map(language => (
                <MenuItem key={language.value} value={language.value}>
                  {language.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="standard"
            style={{
              minWidth: 140,
              width: 'auto',
            }}
          >
            <Select
              labelId="theme-select-label"
              id="theme-select"
              value={selectedTheme}
              onChange={handleThemeChange}
              label="Theme"
            >
              {Object.keys(syntaxThemes).map(themeName => (
                <MenuItem key={themeName} value={themeName}>
                  {themeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <TextField
          label={`${
            languages.find(lang => lang.value === selectedLanguage)?.label
          } Code`}
          id="input"
          multiline
          onChange={handleChange}
          fullWidth
          value={input}
          inputProps={{ style: { resize: 'vertical' } }}
          minRows={3}
          maxRows={20}
          variant="outlined"
        />
      </Grid>

      {input && (
        <>
          <Grid item xs={12}>
            <Divider style={{ margin: theme.spacing(2, 0) }} />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Code Snippet Preview</Typography>
              <Box style={{ display: 'flex', gap: theme.spacing(1) }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileCopyIcon />}
                  onClick={copyToClipboard}
                  color={copySuccess ? 'primary' : 'default'}
                  disabled={imageExporting}
                >
                  {copySuccess ? 'Copied!' : 'Copy Code'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ImageIcon />}
                  onClick={() => exportAsImage(false)}
                  disabled={imageExporting}
                >
                  {imageExporting ? 'Exporting...' : 'Copy as Image'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={() => exportAsImage(true)}
                  disabled={imageExporting}
                >
                  Download Image
                </Button>
              </Box>
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Paper
                ref={codePreviewRef}
                data-code-preview="true"
                elevation={0}
                style={{
                  width: `${optimalWidth}px`,
                  maxWidth: `${optimalWidth}px`,
                  minWidth: '300px',
                  overflow: 'hidden',
                  transition: 'width 0.3s ease-in-out',
                }}
              >
                <WindowHeader fileName={fileName} />
                <Box
                  style={{
                    position: 'relative',
                    overflow: 'auto',
                    width: '100%',
                  }}
                >
                  <SyntaxHighlighter {...highlighterProps} />
                </Box>
              </Paper>
            </Box>
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Box
          display="flex"
          alignItems="center"
          style={{
            color: theme.palette.text.secondary,
          }}
        >
          <InfoIcon
            style={{
              marginRight: theme.spacing(1),
              fontSize: '1rem',
            }}
          />
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary }}
          >
            Select a programming language and theme, then input your code. The
            formatted snippet preview will be generated automatically.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
